import React, {useState, useEffect} from "react";
import {
	Box, Typography, Chip, Button, IconButton, TextField, Avatar,
	Tooltip, Card, CardContent, useMediaQuery, Snackbar,
	Dialog, DialogTitle, DialogContent, DialogActions, Container,
	Backdrop, CircularProgress, Paper, Stepper, Step, StepLabel, Grid,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {ThemeProvider} from "@mui/material/styles";
import {
	ArrowBack, Done, OpenInNew, Pause,
	AccessTime, History as HistoryIcon,
	Message as MessageIcon, LocalShipping as LocalShippingIcon,
	AccountBalance as AccountBalanceIcon,
	SwapHoriz as SwapHorizIcon,
	QrCode2 as QrCode2Icon,
	AccountBalanceWallet as AccountBalanceWalletIcon,
	Apps as AppsIcon,
} from "@mui/icons-material";
import {useTheme} from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {useDispatch} from "react-redux";
import {fetchOrdersByDate} from "../../store/ordersSlice";
import {format} from "date-fns";
import {buttonTheme} from "../../theme";
import {config} from "../../config";
import {getVendorId, getSessionToken, getUserName} from "../../utils/authSession";
import {isStorePickup, normalizeFulfillmentType} from "../../utils/fulfillment";

dayjs.extend(utc);
dayjs.extend(timezone);


const DELIVERY_STEPS = ["Paid", "Processing", "Shipped", "Delivered"];
const PICKUP_STEPS = ["Paid", "Processing", "Ready for Pickup", "Picked Up"];

function getSteps(fulfillmentType) {
	return normalizeFulfillmentType(fulfillmentType) === "PICKUP"
		? PICKUP_STEPS
		: DELIVERY_STEPS;
}

const deriveOrderDate = (payload, metadata, fallbackDate) => {
	const candidates = [
		payload?.payment?.transactionDate,
		payload?.orderDate,
		metadata?.timestamp,
		fallbackDate,
	];
	for (const candidate of candidates) {
		if (candidate && dayjs(candidate).isValid()) {
			return candidate;
		}
	}
	return null;
};

function getActiveStep(orderStatus, fulfillmentType) {
	let index = 0;
	switch (orderStatus) {
		case "NEW":
			index = 0;
			break;
		case "PROCESSING":
		case "PICKED":
		case "PACKED":
			index = 1;
			break;
		case "SHIPPED":
		case "OUT_FOR_DELIVERY":
		case "READY_FOR_PICKUP":
			index = 2;
			break;
		case "DELIVERED":
		case "PICKED_UP":
			index = 3;
			break;
		default:
			index = 0;
	}
	const maxIndex = getSteps(fulfillmentType).length - 1;
	return Math.max(0, Math.min(index, maxIndex));
}

const statusLabels = {
	NEW: "New",
	PROCESSING: "Processing",
	PICKED: "Picked",
	PACKED: "Packed",
	READY_FOR_PICKUP: "Ready for Pickup",
	PICKED_UP: "Picked Up",
	SHIPPED: "Shipped",
	OUT_FOR_DELIVERY: "Out for Delivery",
	DELIVERED: "Delivered",
	CANCELLED: "Cancelled",
	ON_HOLD: "On Hold",
	RETURNED: "Returned",
};

const itemStatusLabels = {
	NOT_PICKED: "Not Picked",
	PICKED: "Picked",
	HOLD: "On Hold",
	NO_STOCK: "No Stock",
};

function getStatusStyles(status) {
	switch (status) {
		case "NEW":
			return {backgroundColor: "#2196F3", color: "#FFFFFF"};
		case "PROCESSING":
			return {backgroundColor: "#FB8C00", color: "#FFFFFF"};
		case "PICKED":
			return {backgroundColor: "#388E3C", color: "#FFFFFF"};
		case "PACKED":
			return {backgroundColor: "#7B1FA2", color: "#FFFFFF"};
		case "READY_FOR_PICKUP":
			return {backgroundColor: "#FBC02D", color: "#FFFFFF"};
		case "PICKED_UP":
			return {backgroundColor: "#8BC34A", color: "#FFFFFF"};
		case "SHIPPED":
			return {backgroundColor: "#673AB7", color: "#FFFFFF"};
		case "OUT_FOR_DELIVERY":
			return {backgroundColor: "#009688", color: "#FFFFFF"};
		case "DELIVERED":
			return {backgroundColor: "#4CAF50", color: "#FFFFFF"};
		case "CANCELLED":
			return {backgroundColor: "#F44336", color: "#FFFFFF"};
		case "ON_HOLD":
			return {backgroundColor: "#FFB300", color: "#FFFFFF"};
		case "RETURNED":
			return {backgroundColor: "#ff4784", color: "#FFFFFF"};
		default:
			return {backgroundColor: "#9E9E9E", color: "#FFFFFF"};
	}
}

function getItemStatusStyles(status) {
	switch (status) {
		case "NOT_PICKED":
			return {backgroundColor: "#9E9E9E", color: "#FFFFFF"};
		case "PICKED":
			return {backgroundColor: "#388E3C", color: "#FFFFFF"};
		case "HOLD":
			return {backgroundColor: "#FFB300", color: "#FFFFFF"};
		case "NO_STOCK":
			return {backgroundColor: "#F44336", color: "#FFFFFF"};
		default:
			return {backgroundColor: "#757575", color: "#FFFFFF"};
	}
}

const API_BASE_URL = config.API_MWSSTOREFRONT_BASE_URL;

const getPaymentStatusInfo = (payment) => {
	const statusRaw = payment?.status
		? payment.status.toString().trim().toUpperCase()
		: "";
	if (!statusRaw) {
		return {
			label: "Unknown",
			color: {bg: "#757575", text: "#FFFFFF"},
		};
	}

	// Explicitly check for known success statuses from API
	const completedKeywords = ["COMPLETED", "SUCCESS", "PAID", "UPI_COMPLETED", "UPI_SUCCESS"];
	if (completedKeywords.some((keyword) => statusRaw.includes(keyword))) {
		return {
			label: "Completed",
			color: {bg: "#2E7D32", text: "#FFFFFF"},
		};
	}

	// Explicitly check for known pending statuses
	const pendingKeywords = ["PENDING", "AWAIT", "AWAITING", "IN_PROGRESS", "FAILED", "PROCESSING"];
	if (pendingKeywords.some((keyword) => statusRaw.includes(keyword))) {
		return {
			label: "Pending",
			color: {bg: "#FB8C00", text: "#FFFFFF"},
		};
	}

	return {
		label: statusRaw.replace(/_/g, " "),
		color: {bg: "#2196F3", text: "#FFFFFF"},
	};
};

const formatCurrencyDisplay = (amount, currency = "INR") => {
	const numericAmount = Number(amount);
	if (Number.isNaN(numericAmount)) {
		return amount ?? "-";
	}
	try {
		return new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
		}).format(numericAmount);
	} catch (error) {
		return `${currency} ${numericAmount.toFixed(2)}`;
	}
};

function validateStatusTransition(currentStatus, newStatus, isPickup) {
	switch (currentStatus) {
		case "NEW":
			return ["PROCESSING", "CANCELLED", "ON_HOLD"].includes(newStatus);
		case "PROCESSING":
			return ["PICKED", "CANCELLED", "ON_HOLD"].includes(newStatus);
		case "PICKED":
			return ["PACKED", "CANCELLED", "ON_HOLD"].includes(newStatus);
		case "PACKED":
			if (isPickup === null || isPickup === undefined) {
				return ["CANCELLED", "ON_HOLD"].includes(newStatus);
			}
			return isPickup
				? ["READY_FOR_PICKUP", "CANCELLED", "ON_HOLD"].includes(newStatus)
				: ["SHIPPED", "CANCELLED", "ON_HOLD"].includes(newStatus);
		case "READY_FOR_PICKUP":
			return ["PICKED_UP", "CANCELLED", "ON_HOLD"].includes(newStatus);
		case "SHIPPED":
			return ["OUT_FOR_DELIVERY", "RETURNED", "ON_HOLD"].includes(newStatus);
		case "OUT_FOR_DELIVERY":
			return ["DELIVERED", "RETURNED", "ON_HOLD"].includes(newStatus);
		case "DELIVERED":
			return newStatus === "RETURNED";
		case "ON_HOLD":
			return ["PROCESSING", "CANCELLED"].includes(newStatus);
		case "CANCELLED":
		case "RETURNED":
			return false;
		default:
			return false;
	}
}

function getActionLabel(status) {
	switch (status) {
		case "PROCESSING":
			return "Process Order";
		case "PICKED":
			return "Pick Order";
		case "PACKED":
			return "Pack Order";
		case "READY_FOR_PICKUP":
			return "Ready for Pickup";
		case "PICKED_UP":
			return "Confirm Pickup";
		case "SHIPPED":
			return "Ship Order";
		case "OUT_FOR_DELIVERY":
			return "Out for Delivery";
		case "DELIVERED":
			return "Confirm Delivery";
		case "ON_HOLD":
			return "Place On Hold";
		case "CANCELLED":
			return "Cancel Order";
		default:
			return statusLabels[status] || status;
	}
}

function getNextAction(order) {
	if (!order) return null;
	const {orderStatus} = order;
	const pickup = isStorePickup(order);
	let next = [];
	switch (orderStatus) {
		case "NEW":
			next = ["PROCESSING"];
			break;
		case "PROCESSING":
			next = ["PICKED"];
			break;
		case "PICKED":
			next = ["PACKED"];
			break;
		case "PACKED":
			next = pickup === null ? [] : pickup ? ["READY_FOR_PICKUP"] : ["SHIPPED"];
			break;
		case "READY_FOR_PICKUP":
			next = ["PICKED_UP"];
			break;
		case "SHIPPED":
			next = ["OUT_FOR_DELIVERY"];
			break;
		case "OUT_FOR_DELIVERY":
			next = ["DELIVERED"];
			break;
		default:
			next = [];
	}
	return next[0] || null;
}

const OrderDetails = ({open, onClose, order, selectedDate}) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [orderDetails, setOrderDetails] = useState(null);
	const [notes, setNotes] = useState("");
	const [originalNotes, setOriginalNotes] = useState("");
	const [isEditingNotes, setIsEditingNotes] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [orderHistory, setOrderHistory] = useState([]);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [nextAction, setNextAction] = useState("");
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [communicationOpen, setCommunicationOpen] = useState(false);
	const [holdDialogOpen, setHoldDialogOpen] = useState(false);
	const [holdItemReason, setHoldItemReason] = useState("");
	const [holdItemId, setHoldItemId] = useState(null);
	const [loading, setLoading] = useState(false);

	const vendorId = getVendorId();
	const buildUrl = (path) =>
		`${API_BASE_URL}/storefront/orm/${encodeURIComponent(vendorId)}${path}`;
	const authHeaders = () => ({"X-Session-Token": sessionToken()});

	const sessionToken = getSessionToken;

	const getNextStatusButtonStyles = (status) => {
		const style = getStatusStyles(status);
		return {
			backgroundColor: style.backgroundColor,
			color: style.color,
			boxShadow: 'none',
			textTransform: 'none',
			fontWeight: 600,
			minWidth: 175,
			width: isMobile ? "100%" : "auto",
			':hover': {
				backgroundColor: style.backgroundColor,
				filter: 'brightness(0.9)',
			},

		};
	};

	const canEditItems =
		orderDetails?.orderStatus === "NEW" ||
		orderDetails?.orderStatus === "PROCESSING";

	const fetchOrderDetails = async (orderId) => {
		try {
			setLoading(true);
			const response = await fetch(buildUrl(`/orders/${orderId}`), {
				headers: authHeaders(),
			});
			if (response.ok) {
				const json = await response.json();
				const data = json?.data ?? json;
				const metadata = json?.metadata ?? {};
				const normalizedFulfillmentType = normalizeFulfillmentType(
					data.fulfillmentType
				);
				const paymentInfo = data.payment ? {...data.payment} : null;
				const orderDate = deriveOrderDate(data, metadata, order?.dateOfOrder);
				const items = Array.isArray(data.items) ? data.items : [];
				const shippingAddress = data.shippingAddress ?? null;
				const customer = data.customer ?? order?.customer ?? null;
				const storeName = data.storeName || order?.storeName || "Store Name";
				const paymentHistory = Array.isArray(data.paymentHistory)
					? data.paymentHistory
					: [];
				const orderNumber =
					data.orderNumber || order?.orderNumber || data.orderId;
				setOrderDetails({
					orderId: data.orderId,
					orderNumber,
					orderStatus: data.orderStatus,
					fulfillmentType: normalizedFulfillmentType,
					shippingAddress,
					payment: paymentInfo,
					customer,
					items,
					storeName,
					orderDate,
					paymentHistory,
				});
				setNotes("");
				setOriginalNotes("");
			} else {
				console.error("Failed to fetch order details");
			}
		} catch (error) {
			console.error("Error fetching order details:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (order && open) {
			const loadOrderData = async () => {
				try {
					setLoading(true);
					const orderId = order.id;
					const notesData = await fetchOrderNotes(orderId);
					await fetchOrderDetails(orderId);
					await fetchOrderHistory(orderId);
					setOrderDetails((prev) => (prev ? {...prev, notes: notesData} : prev));
				} catch (err) {
					console.error("Error loading order data:", err);
				} finally {
					setLoading(false);
				}
			};
			loadOrderData();
		}
	}, [order, open]);

	const fetchOrderHistory = async (orderId) => {
		try {
			setLoading(true);
			const response = await fetch(buildUrl(`/orders/${orderId}/history`), {
				headers: authHeaders(),
			});
			if (response.ok) {
				const data = await response.json();
				setOrderHistory(data);
			} else {
				console.error("Failed to fetch order history");
			}
		} catch (error) {
			console.error("Error fetching order history:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchOrderNotes = async (orderId) => {
		try {
			setLoading(true);
			const response = await fetch(buildUrl(`/orders/${orderId}/getOrderNotes`), {
				headers: authHeaders(),
			});
			if (response.ok) {
				const json = await response.json();
				const notes = json?.data?.notes ?? json?.notes ?? [];
				return Array.isArray(notes) ? notes : [];
			} else {
				console.error("Failed to fetch order notes");
				return [];
			}
		} catch (error) {
			console.error("Error fetching order notes:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const updateItemStatus = async (orderId, itemId, newStatus, reason = "") => {
		try {
			setLoading(true);
			let endpoint = buildUrl(`/orders/${orderId}/items/${itemId}/status?status=${newStatus}`);
			if (reason) endpoint += `&reason=${encodeURIComponent(reason)}`;
			endpoint += `&username=${encodeURIComponent(getUserName())}`;
			const response = await fetch(endpoint, {method: "POST", headers: authHeaders()});
			if (!response.ok) {
				console.error("Failed to update item status");
				setSnackbarMessage("Failed to update item status");
				setSnackbarOpen(true);
				return false;
			}
			setSnackbarMessage(`Item #${itemId} status updated to '${newStatus}'`);
			setSnackbarOpen(true);
			await fetchOrderDetails(orderId);
			return true;
		} catch (error) {
			console.error("Error updating item status:", error);
			setSnackbarMessage("Error updating item status");
			setSnackbarOpen(true);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const addOrderNotes = async (
		orderId,
		notesText,
		userName = getUserName(),
		notifyCustomer = "yes",
		orderStatus = orderDetails.orderStatus
	) => {
		try {
			setLoading(true);
			const response = await fetch(
				buildUrl(`/orders/${orderId}/notes`),
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Session-Token": sessionToken()
					},
					body: JSON.stringify({
						notes: notesText,
						userName,
						notifyCustomer,
						orderStatus,
					}),
				}
			);
			if (!response.ok) {
				console.error("Failed to add notes to the order");
				setSnackbarMessage("Failed to add notes.");
				setSnackbarOpen(true);
				return false;
			}
			setSnackbarMessage("Notes added successfully and customer notified.");
			setSnackbarOpen(true);
			return true;
		} catch (error) {
			console.error("Error adding notes:", error);
			setSnackbarMessage("Error adding notes.");
			setSnackbarOpen(true);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const handleNotesChange = (e) => {
		setNotes(e.target.value);
		setIsEditingNotes(true);
	};

	const handleSaveNotes = async () => {
		if (orderDetails && orderDetails.orderId) {
			const success = await addOrderNotes(
				orderDetails.orderId,
				notes,
				getUserName(),
				"yes",
				orderDetails.orderStatus
			);
			if (success) {
				setOriginalNotes(notes);
				setIsEditingNotes(false);
				const updatedNotes = await fetchOrderNotes(orderDetails.orderId);
				setOrderDetails((prev) => (prev ? {...prev, notes: updatedNotes} : prev));
				setNotes("");
			}
		}
	};

	const handleCancelNotes = () => {
		setNotes("");
		setIsEditingNotes(false);
	};

	const handleNextActionClick = () => {
		const action = getNextAction(orderDetails);
		if (action === "PICKED") {
			const allItemsPicked = orderDetails.items.every((i) => i.status === "PICKED");
			if (!allItemsPicked) {
				setSnackbarMessage(
					"All items must be marked as PICKED before picking the order."
				);
				setSnackbarOpen(true);
				return;
			}
		}
		setNextAction(action);
		setOpenConfirmDialog(true);
	};

	const handleConfirmNextAction = async () => {
		if (!orderDetails) return;
		const pickup = isStorePickup(orderDetails);
		const cs = orderDetails.orderStatus;
		const ns = nextAction;
		const oid = orderDetails.orderId;
		if (!validateStatusTransition(cs, ns, pickup)) {
			setSnackbarMessage(`Invalid status transition from ${cs} to ${ns}`);
			setSnackbarOpen(true);
			setOpenConfirmDialog(false);
			return;
		}
		try {
			setLoading(true);
			let endpoint = "";
			const username = getUserName();
			switch (ns) {
				case "PROCESSING":
					endpoint = buildUrl(`/orders/${oid}/process?username=${username}`);
					break;
				case "PICKED":
					endpoint = buildUrl(`/orders/${oid}/pick?username=${username}`);
					break;
				case "PACKED":
					endpoint = buildUrl(`/orders/${oid}/pack?username=${username}`);
					break;
				case "READY_FOR_PICKUP":
					endpoint = buildUrl(`/orders/${oid}/readyForPickup?username=${username}`);
					break;
				case "PICKED_UP":
					endpoint = buildUrl(`/orders/${oid}/pickup?username=${username}`);
					break;
				case "SHIPPED":
					endpoint = buildUrl(`/orders/${oid}/ship?username=${username}`);
					break;
				case "OUT_FOR_DELIVERY":
					endpoint = buildUrl(`/orders/${oid}/outForDelivery?username=${username}`);
					break;
				case "DELIVERED":
					endpoint = buildUrl(`/orders/${oid}/deliver?username=${username}`);
					break;
				case "ON_HOLD":
					endpoint = buildUrl(`/orders/${oid}/hold?username=${username}`);
					handleSaveNotes();
					break;
				case "CANCELLED":
					endpoint = buildUrl(`/orders/${oid}/cancel?username=${username}`);
					handleSaveNotes();
					break;
				default:
					console.error("Unknown next status:", ns);
					setSnackbarMessage(`Unknown next status: ${ns}`);
					setSnackbarOpen(true);
					return;
			}
			const r = await fetch(endpoint, {method: "POST", headers: authHeaders()});
			if (r.ok) {
				setOrderDetails((prev) => (prev ? {...prev, orderStatus: ns} : prev));
				await fetchOrderHistory(oid);
				if (selectedDate) {
					dispatch(
						fetchOrdersByDate({
							date: selectedDate.format("YYYY-MM-DD"),
							forceRefresh: true,
						})
					);
				}
				setSnackbarMessage(`Order ${oid} status updated to ${ns}`);
				setSnackbarOpen(true);
			} else {
				console.error("Failed to update order status");
				setSnackbarMessage("Failed to update order status");
				setSnackbarOpen(true);
			}
		} catch (error) {
			console.error("Error updating order status:", error);
			setSnackbarMessage("Error updating order status");
			setSnackbarOpen(true);
		} finally {
			setLoading(false);
		}
		setOpenConfirmDialog(false);
		setNextAction("");
	};

	const handleCancelNextAction = () => {
		setOpenConfirmDialog(false);
		setNextAction("");
	};

	const handleCloseDialog = () => onClose();

	const handleOpenHistory = () => setHistoryOpen(true);
	const handleCloseHistory = () => setHistoryOpen(false);
	const handleSnackbarClose = () => setSnackbarOpen(false);

	const handleOpenCommunication = () => setCommunicationOpen(true);
	const handleCloseCommunication = () => {
		setNotes("");
		setIsEditingNotes(false);
		setCommunicationOpen(false);
	};

	const handleHoldOrderClick = () => {
		setNotes("Your order has been placed on hold. We will update you shortly.");
		setNextAction("ON_HOLD");
		setOpenConfirmDialog(true);
	};

	const handleCancelOrderClick = () => {
		setNotes("Your order has been cancelled. We apologize for the inconvenience.");
		setNextAction("CANCELLED");
		setOpenConfirmDialog(true);
	};

	const handleOpenHoldItemDialog = (itemId) => {
		setHoldItemId(itemId);
		setHoldItemReason("");
		setHoldDialogOpen(true);
	};
	const handleCloseHoldItemDialog = () => {
		setHoldDialogOpen(false);
		setHoldItemId(null);
		setHoldItemReason("");
	};
	const handleConfirmHoldItem = async () => {
		if (holdItemId && holdItemReason.trim()) {
			await updateItemStatus(
				orderDetails.orderId,
				holdItemId,
				"HOLD",
				holdItemReason.trim()
			);
		}
		handleCloseHoldItemDialog();
	};

	if (!orderDetails) return null;
	const statusLabel = statusLabels[orderDetails.orderStatus] || orderDetails.orderStatus;
	const orderTimestamp = orderDetails.orderDate ? dayjs(orderDetails.orderDate) : null;
	const currentTime = dayjs();
	const slaDuration = 4;
	let slaStatusText = "On Track";
	let slaStatusColor = "#28a745";
	if (orderTimestamp) {
		const slaDeadline = orderTimestamp.add(slaDuration, "hour");
		if (currentTime.isAfter(slaDeadline)) {
			const hoursBreached = currentTime.diff(slaDeadline, "hour");
			slaStatusText = `Breached by ${hoursBreached} hours`;
			slaStatusColor = "#dc3545";
		}
	}
	const isPickupOrder = isStorePickup(orderDetails);
	const paymentStatusInfo = getPaymentStatusInfo(orderDetails.payment);
	const paymentDetails = orderDetails.payment ?? {};
	const billingAddress = paymentDetails?.billingAddress;
	const formattedAmount = paymentDetails?.amountPaid
		? formatCurrencyDisplay(paymentDetails.amountPaid, paymentDetails.currency || "INR")
		: null;
	const paymentDateDisplay =
		paymentDetails?.transactionDate &&
		paymentDetails.transactionDate !== "N/A" &&
		dayjs(paymentDetails.transactionDate).isValid()
			? dayjs.utc(paymentDetails.transactionDate).local().format("MMM D, YYYY, h:mm A")
			: null;
	const upiDetails = paymentDetails?.upiDetails ?? null;
	const paymentReference =
		paymentDetails?.transactionId ||
		paymentDetails?.referenceId ||
		upiDetails?.upiTransactionId ||
		null;
	const upiInfoItems = upiDetails
		? [
			{
				label: "Payee UPI",
				value: upiDetails.payeeUpiId,
				icon: AccountBalanceIcon,
			},
			{
				label: "Payer UPI",
				value: upiDetails.payerUpiId,
				icon: SwapHorizIcon,
			},
			{
				label: "UPI Transaction ID",
				value: upiDetails.upiTransactionId,
				icon: QrCode2Icon,
			},
			{
				label: "Bank",
				value: upiDetails.bankName,
				icon: AccountBalanceWalletIcon,
			},
			{
				label: "App Used",
				value: upiDetails.appUsed,
				icon: AppsIcon,
			},
		].filter((item) => Boolean(item.value))
		: [];
	const paymentModeLabel = paymentDetails?.method
		? paymentDetails.method.toUpperCase()
		: "CARD / UPI";
	const billingAddressLine = billingAddress
		? [
			billingAddress.street,
			billingAddress.city,
			billingAddress.state,
			billingAddress.postalCode,
		]
			.filter(Boolean)
			.join(", ")
		: "";
	const paymentSurfaceBg =
		theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : theme.palette.grey[50];
	const upiSurfaceBg =
		theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : theme.palette.grey[100];
	const hasBillingAddress = Boolean(billingAddressLine);
	const hasUpiDetails = upiInfoItems.length > 0;

	const stepIndex = getActiveStep(orderDetails.orderStatus, orderDetails.fulfillmentType);
	const isTerminal = ["DELIVERED", "PICKED_UP"].includes(orderDetails.orderStatus);
	const steps = getSteps(orderDetails.fulfillmentType);

	return (
		<Box>
			<ThemeProvider theme={theme}>
				<Backdrop
					sx={{color: "#fff", zIndex: (th) => th.zIndex.drawer + 999}}
					open={loading}
				>
					<CircularProgress color="inherit"/>
				</Backdrop>

				<Dialog open={open} onClose={handleCloseDialog} fullScreen>
					<Container
						maxWidth="lg"
						sx={{
							display: "flex",
							alignItems: "center",
							p: 2,
							borderBottom: `1px solid ${theme.palette.divider}`,
						}}
					>
						<IconButton onClick={handleCloseDialog}>
							<ArrowBack/>
						</IconButton>
						<Typography variant="h6" sx={{ml: 1, fontWeight: "bold"}}>
							Order Details
						</Typography>
					</Container>

					<Container maxWidth="md" sx={{py: 2}}>
						<Paper elevation={1} sx={{mb: 2, p: isMobile ? 1 : 2}}>
							<Typography variant="h6" sx={{mb: 1}}>
								Order Progress
							</Typography>
							<Stepper
								activeStep={isTerminal ? -1 : stepIndex}
								alternativeLabel
							>
								{steps.map((label, idx) => {
									const isCurrentStep = idx === stepIndex;
									const tooltipTitle = isCurrentStep
										? `Current Status: ${statusLabel}`
										: "";
									return (
										<Step
											key={label}
											completed={isTerminal ? idx <= stepIndex : idx < stepIndex}
										>
											<Tooltip title={tooltipTitle} arrow>
												<StepLabel>{label}</StepLabel>
											</Tooltip>
										</Step>
									);
								})}
							</Stepper>
						</Paper>

						<Grid container spacing={2} sx={{mb: 2}}>
							<Grid item xs={12} md={4}>
								<Box sx={{display: "flex", flex: 1, flexDirection: "column", gap: 2, height: "-webkit-fill-available"}}>
									<Card sx={{flex: 1, position: "relative"}}>
										<CardContent sx={{p: isMobile ? 1 : 2, pb: isMobile ? "8px!important" : "16px!important"}}>
											<Box
												sx={{
													display: "flex",
													flexDirection: "row",
													flexWrap: "wrap",
													justifyContent: "space-between",
													alignItems: "center",
													gap: 1
												}}
											>
												<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
													Order Information
												</Typography>
												<Chip
													label={statusLabel}
													sx={{
														...getStatusStyles(orderDetails.orderStatus),
														fontWeight: 500,
														borderRadius: 5,
													}}
												/>
											</Box>
											<Typography variant="body2" sx={{mt: 1, fontWeight: 'bold'}}>
												{orderDetails.orderNumber}
											</Typography>
											<Typography variant="body2" sx={{mt: 1}}>
												Buyer: <span style={{fontWeight: 'bold'}}>{orderDetails.customer?.name || "N/A"}</span>
											</Typography>
											{orderDetails.orderDate && (
												<Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>
													{dayjs.utc(orderDetails.orderDate).local().format("MMM D, YYYY, h:mm A")}
												</Typography>
											)}
											<Box sx={{display: "flex", alignItems: "center", mt: 1}}>
												<AccessTime sx={{mr: 0.5, color: slaStatusColor}}/>
												<Typography
													variant="caption"
													sx={{fontWeight: "bold", color: slaStatusColor}}
												>
													SLA Status: {slaStatusText}
												</Typography>
											</Box>
										</CardContent>
									</Card>
									<Card sx={{flex: 1}}>
										<CardContent sx={{p: isMobile ? 1 : 2, pb: isMobile ? "8px!important" : "16px!important"}}>
											<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
												{isPickupOrder ? "Pickup" : "Shipping"}
											</Typography>
											<Chip
												label={
													isPickupOrder ? "Store Pickup" : "Shipping"
												}
												color="primary"
												size="small"
												icon={<LocalShippingIcon fontSize="small"/>}
											/>
											{orderDetails.shippingAddress ? (
												<Typography variant="body2" sx={{mt: 1}}>
													{orderDetails.shippingAddress.street},{" "}
													{orderDetails.shippingAddress.city},{" "}
													{orderDetails.shippingAddress.state}{" "}
													{orderDetails.shippingAddress.postalCode}
												</Typography>
											) : (
												<Typography variant="body2" sx={{mt: 1}}>
													{isPickupOrder
														? "No pickup location provided."
														: "No shipping address found."}
												</Typography>
											)}
										</CardContent>
									</Card>
								</Box>
							</Grid>
							<Grid item xs={12} md={8}>
								<Card sx={{height: "100%"}}>
									<CardContent sx={{p: isMobile ? 1 : 3}}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												flexWrap: "wrap",
												gap: 1,
											}}
										>
											<Box>
												<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
													Payment
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Method
												</Typography>
												<Typography variant="body1" sx={{fontWeight: 700}}>
													{paymentModeLabel}
												</Typography>
											</Box>
											<Chip
												label={paymentStatusInfo.label}
												size="small"
												sx={{
													backgroundColor: paymentStatusInfo.color.bg,
													color: paymentStatusInfo.color.text,
													fontWeight: 600
												}}
											/>
										</Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "baseline",
												justifyContent: "space-between",
												flexWrap: "wrap",
												gap: 2,
												mt: 2,
											}}
										>
											<Box>
												<Typography variant="h4" sx={{fontWeight: 700}}>
													{formattedAmount || "—"}
												</Typography>
												{paymentDateDisplay && (
													<Typography variant="caption" color="text.secondary">
														Processed on {paymentDateDisplay}
													</Typography>
												)}
											</Box>
											{paymentReference && (
												<Box sx={{textAlign: isMobile ? "left" : "right"}}>
													<Typography variant="caption" color="text.secondary">
														Reference ID
													</Typography>
													<Typography variant="body2" sx={{fontWeight: 600}}>
														{paymentReference}
													</Typography>
												</Box>
											)}
										</Box>

										<Grid container spacing={2} sx={{mt: 2}}>
											{hasBillingAddress && (
												<Grid item xs={12} md={4}>
													<Box
														sx={{
															height: "100%",
															p: 1.5,
															borderRadius: 2,
															backgroundColor: paymentSurfaceBg,
															border: `1px solid ${theme.palette.divider}`,
														}}
													>
														<Typography variant="caption" color="text.secondary">
															Billed To
														</Typography>
														<Typography variant="body2" sx={{fontWeight: 600}}>
															{billingAddress.recipientName || "Billing Contact"}
														</Typography>
														<Typography
															variant="body2"
															sx={{mt: 0.5, whiteSpace: "pre-line"}}
														>
															{billingAddressLine}
														</Typography>
													</Box>
												</Grid>
											)}
											{hasUpiDetails && (
												<Grid item xs={12} md={hasBillingAddress ? 8 : 12}>
													<Box
														sx={{
															height: "100%",
															p: 1.5,
															borderRadius: 2,
															backgroundColor: upiSurfaceBg,
															border: `1px solid ${theme.palette.divider}`,
														}}
													>
														<Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
															<Avatar
																variant="rounded"
																sx={{
																	width: 30,
																	height: 30,
																	backgroundColor: theme.palette.primary.main,
																	color: theme.palette.primary.contrastText,
																}}
															>
																<QrCode2Icon fontSize="small"/>
															</Avatar>
															<Box>
																<Typography variant="body2" sx={{fontWeight: 700}}>
																	UPI Snapshot
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{upiDetails?.appUsed || "UPI"}
																</Typography>
															</Box>
														</Box>
														<Grid container spacing={1.5}>
															{upiInfoItems.map((item) => {
																const IconComponent = item.icon;
																return (
																	<Grid item xs={12} sm={6} key={item.label}>
																		<Box
																			sx={{
																				display: "flex",
																				gap: 1,
																				alignItems: "flex-start",
																			}}
																		>
																			<Avatar
																				variant="rounded"
																				sx={{
																					width: 26,
																					height: 26,
																					backgroundColor:
																						theme.palette.mode === "dark"
																							? "rgba(255,255,255,0.1)"
																							: "rgba(0,0,0,0.05)",
																					color: theme.palette.text.primary,
																				}}
																			>
																				<IconComponent fontSize="small"/>
																			</Avatar>
																			<Box sx={{minWidth: 0}}>
																				<Typography variant="caption" color="text.secondary">
																					{item.label}
																				</Typography>
																				<Typography
																					variant="body2"
																					sx={{fontWeight: 600, wordBreak: "break-all"}}
																				>
																					{item.value}
																				</Typography>
																			</Box>
																		</Box>
																	</Grid>
																);
															})}
														</Grid>
													</Box>
												</Grid>
											)}
										</Grid>
										{!hasBillingAddress && !hasUpiDetails && (
											<Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
												No payment details available.
											</Typography>
										)}
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Paper elevation={1} sx={{p: isMobile ? 1 : 2, mb: 2}}>
							<Typography variant="subtitle1" fontWeight="bold" sx={{mb: 1}}>
								Ordered Items (Total: {orderDetails.items.length})
							</Typography>
							<Box
								sx={{
									maxHeight: 400,
									overflowY: "auto",
									pr: 1,
									"&::-webkit-scrollbar": {width: 6},
									"&::-webkit-scrollbar-track": {backgroundColor: "#f1f1f1"},
									"&::-webkit-scrollbar-thumb": {
										backgroundColor: "#ccc",
										borderRadius: 3,
									},
								}}
							>
								{orderDetails.items.map((item, idx) => {
									const itemLabel = itemStatusLabels[item.status] || item.status;
									const itemStyles = getItemStatusStyles(item.status);
									return (
										<Box
											key={idx}
											sx={{
												display: "flex",
												alignItems: "center",
												p: 1,
												mb: 1,
												border: "1px solid #ccc",
												borderRadius: 1,
											}}
										>
											{item.productImage ? (
												<Box
													component="img"
													src={item.productImage}
													alt={item.productName}
													sx={{
														width: 50,
														height: 50,
														mr: 2,
														borderRadius: 1,
														objectFit: "cover",
													}}
												/>
											) : (
												<Avatar
													variant="rounded"
													sx={{
														width: 50,
														height: 50,
														mr: 2,
														bgcolor: "primary.main",
														color: "#fff",
													}}
												>
													{item.productName?.charAt(0).toUpperCase()}
												</Avatar>
											)}
											<Box sx={{flexGrow: 1}}>
												<Typography variant="subtitle2" sx={{fontWeight: "bold"}}>
													{item.productName}
												</Typography>
												{isMobile ? (
													<Typography variant="caption" color="text.secondary">
														<span>Qty: {item.quantity}</span>
														<span>Total: ₹{item.totalPrice.toFixed(2)}</span>
													</Typography>
												) : (
													<Typography variant="caption" color="text.secondary"
													            sx={{display: 'flex', gap: 1}}>
														<span>Quantity: {item.quantity}</span>
														<span>Unit Price: ₹{item.unitPrice.toFixed(2)}</span>
														<span>Total: ₹{item.totalPrice.toFixed(2)}</span>
													</Typography>
												)}
											</Box>
											<Box sx={{mx: 2}}>
												<Chip
													label={itemLabel}
													size="small"
													sx={{...itemStyles, fontWeight: 500}}
												/>
											</Box>
											{canEditItems && (
												<Box
													sx={{
														display: "flex",
														flexDirection: isMobile ? "column" : "row",
														gap: 0.5,
														alignItems: isMobile ? "flex-start" : "center",
													}}
												>
													<Tooltip title="Mark item as Picked">
														<IconButton
															size="small"
															sx={{
																backgroundColor: "#388E3C",
																color: "#FFFFFF",
																"&:hover": {backgroundColor: "#2E7D32"},
															}}
															onClick={async (e) => {
																e.stopPropagation();
																await updateItemStatus(
																	orderDetails.orderId,
																	item.itemId,
																	"PICKED"
																);
															}}
														>
															<Done/>
														</IconButton>
													</Tooltip>
													<Tooltip title="Mark item as No Stock">
														<IconButton
															size="small"
															sx={{
																backgroundColor: "#F44336",
																color: "#FFFFFF",
																"&:hover": {backgroundColor: "#E53935"},
															}}
															onClick={async (e) => {
																e.stopPropagation();
																await updateItemStatus(
																	orderDetails.orderId,
																	item.itemId,
																	"NO_STOCK"
																);
															}}
														>
															<Pause/>
														</IconButton>
													</Tooltip>
													<Tooltip title="Put item on hold (requires reason)">
														<IconButton
															size="small"
															sx={{
																backgroundColor: "#FFB300",
																color: "#FFFFFF",
																"&:hover": {backgroundColor: "#FFA000"},
															}}
															onClick={(e) => {
																e.stopPropagation();
																handleOpenHoldItemDialog(item.itemId);
															}}
														>
															<OpenInNew/>
														</IconButton>
													</Tooltip>
												</Box>
											)}
										</Box>
									);
								})}
							</Box>
							<Box
								sx={{
									mt: 2,
									display: "flex",
									flexWrap: "wrap", width: '100%',
									justifyContent: "space-between",
									alignItems: "center", gap: 2
								}}
							>
								<Box sx={{
									display: "flex",
									gap: 1,
									width: isMobile ? '100%' : 'auto',
									minWidth: isMobile ? "100%" : 300
								}}>
									<Button
										variant="outlined"
										color="secondary"
										size="small" sx={{width: '100%'}}
										onClick={handleOpenHistory}
										startIcon={<HistoryIcon/>}
									>
										View History
									</Button>
									<Button
										variant="contained"
										color="secondary"
										size="small" sx={{width: '100%'}}
										onClick={handleOpenCommunication}
										startIcon={<MessageIcon/>}
									>
										Order Notes
									</Button>
								</Box>
								{getNextAction(orderDetails) &&
									validateStatusTransition(
										orderDetails.orderStatus,
										getNextAction(orderDetails),
										isPickupOrder
									) && (
										<Button
											variant="contained"
											color="primary"
											size="small"
											sx={getNextStatusButtonStyles(getNextAction(orderDetails))}
											onClick={handleNextActionClick}
										>
											{getActionLabel(getNextAction(orderDetails))}
										</Button>
									)}
							</Box>
						</Paper>
					</Container>
				</Dialog>

				<Dialog open={communicationOpen} onClose={handleCloseCommunication} maxWidth="sm" fullWidth
				        fullScreen={isMobile}>
					<DialogTitle>Order Notes</DialogTitle>
					<DialogContent>
						{orderDetails.notes && orderDetails.notes.length === 0 && (
							<Box sx={{mb: 1, p: 1, backgroundColor: "#EFEFEF", borderRadius: 1, boxShadow: 1, width: '100%'}}>
								No Notes added for this order
							</Box>
						)}
						<TextField
							label="Order Notes"
							multiline
							rows={4}
							variant="outlined"
							fullWidth sx={{mt: 1}}
							placeholder="Add notes about this order..."
							value={notes}
							onChange={handleNotesChange}
						/>
						<Box
							sx={{
								mt: 2,
								display: "flex",
								justifyContent: "space-between",
								width: "100%", gap: 1, flexWrap: "wrap"
							}}
						>

							<Box display="flex" gap={1} sx={{width: isMobile ? "100%" : 'fit-content'}}>
								{validateStatusTransition(
									orderDetails.orderStatus,
									"ON_HOLD",
									isPickupOrder
								) && (
									<Button
										variant="contained"
										onClick={handleHoldOrderClick}
										disableElevation
										color="warning"
										sx={{
											borderRadius: 2,
											fontWeight: 600,
											textTransform: "none",
											width: isMobile ? "100%" : "auto"
										}}
									>
										Hold & Notify
									</Button>
								)}
								{validateStatusTransition(
									orderDetails.orderStatus,
									"CANCELLED",
									isPickupOrder
								) && (
									<Button
										variant="contained"
										onClick={handleCancelOrderClick}
										disableElevation
										color="error"
										sx={{
											borderRadius: 2,
											fontWeight: 600,
											textTransform: "none",
											width: isMobile ? "100%" : "auto"
										}}
									>
										Cancel & Notify
									</Button>
								)}
							</Box>
							<Box display="flex" gap={1} sx={{width: isMobile ? "100%" : 'fit-content'}}>
								{isEditingNotes ? (
									<>
										<Button
											variant="outlined"
											onClick={handleCancelNotes}
											color="secondary"
											sx={{
												borderRadius: 2,
												fontWeight: 600,
												textTransform: "none",
												width: isMobile ? "100%" : "auto"
											}}
										>
											Discard
										</Button>
										<Button
											variant="contained"
											color="primary"
											onClick={handleSaveNotes}
											sx={{
												borderRadius: 2,
												fontWeight: 600,
												textTransform: "none",
												width: isMobile ? "100%" : "auto"
											}}
										>
											Update & Notify
										</Button>
									</>
								) : (
									<Button
										variant="contained"
										color="primary"
										onClick={handleCloseCommunication}
										sx={{
											borderRadius: 2,
											fontWeight: 600,
											textTransform: "none",
											width: isMobile ? "100%" : "auto"
										}}
									>
										Close
									</Button>
								)}
							</Box>
						</Box>
						{orderDetails.notes && orderDetails.notes.length > 0 && (
							<Box
								sx={{
									maxHeight: 230,
									overflowY: "auto",
									mt: 2,
									display: 'flex',
									flexDirection: 'column',
									gap: 1,
									borderWidth: 1,
									borderColor: '#bbb',
									borderRadius: 1,
									boxShadow: 2,
									p: 1
								}}>
								{orderDetails.notes.map((n, i) => (
									<Box key={i} sx={{p: 1, backgroundColor: "#F3F3F3", borderRadius: 1, boxShadow: 1}}>
										<Typography variant="body2" sx={{fontWeight: "bold"}}>
											{n.createdBy} - {dayjs.utc(n.createdAt).local().format("MMM D, YYYY, h:mm A")}
										</Typography>
										<Typography variant="body2">
											{n.orderNotes}
										</Typography>
									</Box>
								))}
							</Box>
						)}
					</DialogContent>
				</Dialog>

				<Dialog open={holdDialogOpen} onClose={handleCloseHoldItemDialog}>
					<DialogTitle>Put Item on Hold</DialogTitle>
					<DialogContent>
						<TextField
							autoFocus
							margin="dense"
							label="Reason"
							fullWidth
							variant="outlined"
							value={holdItemReason}
							onChange={(e) => setHoldItemReason(e.target.value)}
						/>
					</DialogContent>
					<DialogActions>
						<Button sx={{fontWeight: 'bold'}} onClick={handleCloseHoldItemDialog} color="error">
							Cancel
						</Button>
						<Button
							onClick={handleConfirmHoldItem}
							color="secondary"
							autoFocus sx={{fontWeight: 'bold'}}
							disabled={!holdItemReason.trim()}
						>
							Confirm
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog
					open={historyOpen}
					onClose={handleCloseHistory}
					maxWidth='sm' fullWidth fullScreen={isMobile}
				>
					<DialogTitle>Order History</DialogTitle>
					<DialogContent sx={{px: isMobile ? 1 : 2}}>
						{orderHistory.length > 0 ? (
							orderHistory.map((event, index) => {
								const label = statusLabels[event.status] || event.status;
								return (
									<Box key={index} sx={{
										gap: 0.5,
										display: 'flex',
										flexDirection: 'column',
										p: 1, mb: 1,
										border: '1px #aaa',
										borderRadius: 2,
										borderStyle: 'solid', boxShadow: 1
									}}>
										<Typography
											variant="subtitle2"
											sx={{
												fontWeight: "bold",
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											{event.action} -
											<Chip
												label={label}
												sx={{
													...getStatusStyles(event.status),
													fontWeight: 500,
													borderRadius: 5,
												}}
											/>
										</Typography>
										<Typography variant="body2" color="textSecondary">
											By {event.actionBy} on {dayjs.utc(event.actionAt).local().format("MMM D, YYYY, h:mm A")}
										</Typography>
										{/*<Typography variant="body2" sx={{mt: 1}}>*/}
										{/*	{event.comments}*/}
										{/*</Typography>*/}
									</Box>
								);
							})
						) : (
							<Typography>No history available for this order.</Typography>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							onClick={handleCloseHistory}
							sx={{
								borderRadius: 2,
								fontWeight: 600,
								textTransform: "none",
								width: isMobile ? "100%" : "auto"
							}} variant="contained"
							color="primary"
						>
							Close
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog open={openConfirmDialog} onClose={handleCancelNextAction}>
					<DialogTitle>Confirm {getActionLabel(nextAction)}</DialogTitle>
					<DialogContent>
						<Typography>
							Are you sure you want to <strong>{getActionLabel(nextAction)}</strong>{" "}
							for Order ID <strong>{orderDetails?.orderId}</strong>?
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCancelNextAction} sx={{fontWeight: 'bold'}} color="error">
							Cancel
						</Button>
						<Button onClick={handleConfirmNextAction} sx={{fontWeight: 'bold'}} color="secondary" autoFocus>
							Confirm
						</Button>
					</DialogActions>
				</Dialog>

				<Snackbar
					open={snackbarOpen}
					autoHideDuration={6000}
					onClose={handleSnackbarClose}
				>
					<MuiAlert
						onClose={handleSnackbarClose}
						severity="success"
						sx={{width: "100%"}}
					>
						{snackbarMessage}
					</MuiAlert>
				</Snackbar>
			</ThemeProvider>
		</Box>
	);
};

export default OrderDetails;
